import React, { useMemo } from "react";
import {
  useApproveDayoffMutation,
  useFetchPermissionListQuery,
  useLazyFetchSystemFormQuery,
} from "../../../store";
import Box from "@mui/material/Box";
import { personelPermissionColumns } from "../utils/personelPermissionColumns";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import {
  openErrorToast,
  openSuccessToast,
} from "../../../store/slices/toast/toastSlice";
import DocumentDialog from "../../../components/shared/DocumentDialog";
import { setSelectedId } from "../../../store/slices/permissionGrid/permissionGridSlice";
import { openDialog } from "../../../store/slices/dialog/dialogSlice";
import { DOCUMENT_DIALOG } from "../../../store/slices/utils/dialogEnum";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Loading from "../../../components/shared/Loading";

const APPROVE_FILTERS = [
  { label: "Tümü", value: "all" },
  { label: "Bekleyen", value: "pending" },
  { label: "Onaylanan", value: "approved" },
  { label: "Reddedilen", value: "rejected" },
];

const PersonelPermissionGrid = () => {
  const dispatch = useDispatch();
  const [searchText, setSearchText] = React.useState("");
  const [approveFilter, setApproveFilter] = React.useState("all");
  const [typeFilter, setTypeFilter] = React.useState("all");

  const [approveDayoff, { isLoading: isApproveLoading }] =
    useApproveDayoffMutation();
  const { documentDialogOpen } = useSelector((state) => state.dialogSlice);

  const [
    systemFormTrigger,
    { data: systemForm, isFetching: isSystemFormLoading },
  ] = useLazyFetchSystemFormQuery();

  // Tüm veriyi çek (9999 limit → frontend filtreleme)
  const { data: permissionList, isLoading: isPermissionLoading } =
    useFetchPermissionListQuery(
      { page: 1, limit: 9999, searchText: "", date: "" },
      { refetchOnMountOrArgChange: true }
    );


  // Frontend filtre + arama
  const filteredRows = useMemo(() => {
    const rows = permissionList?.data?.dayoffs || [];
    return rows.filter((row) => {
      // İsim araması
      const fullName = `${row.name} ${row.surname}`.toLowerCase();
      if (searchText && !fullName.includes(searchText.toLowerCase())) return false;

      // Onay durumu filtresi
      if (approveFilter !== "all") {
        if (approveFilter === "pending" && row.isApprove !== null) return false;
        if (approveFilter === "approved" && row.isApprove !== true) return false;
        if (approveFilter === "rejected" && row.isApprove !== false) return false;
      }

      // İzin tipi filtresi
      if (typeFilter !== "all" && row.dayoffTypeName !== typeFilter) return false;

      return true;
    });
  }, [permissionList?.data?.dayoffs, searchText, approveFilter, typeFilter]);

  // Benzersiz izin tiplerini çıkar (API'dan dönen veriden)
  const uniqueTypes = useMemo(() => {
    const rows = permissionList?.data?.dayoffs || [];
    const types = [...new Set(rows.map((r) => r.dayoffTypeName).filter(Boolean))];
    return types;
  }, [permissionList?.data?.dayoffs]);

  const handleApproveOrReject = async ({ id, isApprove }) => {
    const res = await approveDayoff({ dayoffId: id, isApprove });
    if (res) {
      if (!res.data.isSuccess) {
        dispatch(openErrorToast(res.message));
      } else {
        dispatch(openSuccessToast("İşlem Tamamlandı"));
      }
    }
  };

  const handleDocument = (id) => {
    dispatch(setSelectedId(id));
    dispatch(openDialog(DOCUMENT_DIALOG));
  };

  const handleSystemForm = (id) => {
    systemFormTrigger(id);
  };

  React.useEffect(() => {
    if (systemForm && systemForm.data?.htmlData) {
      const fileURL = `data:application/pdf;base64,${systemForm.data.htmlData}`;
      window.open(fileURL, "_blank");
    }
  }, [systemForm]);

  if (isSystemFormLoading) return <Loading isLoading={isSystemFormLoading} />;

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
        mt: { xs: 7, sm: 8 },
        minWidth: { xs: 600, sm: 0 },
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {documentDialogOpen === true && <DocumentDialog />}

      {/* Arama + Filtreler */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 1 }}>
        {/* Search */}
        <TextField
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Personel adı ara..."
          size="small"
          sx={{ width: { xs: "100%", sm: 320 }, bgcolor: "#fff", borderRadius: 2, boxShadow: 1 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "grey.500" }} />
                </InputAdornment>
              ),
            },
          }}
        />

        {/* Onay Durumu Filtresi */}
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: "text.secondary" }}>
            Onay Durumu
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {APPROVE_FILTERS.map((f) => (
              <Chip
                key={f.label}
                label={f.label}
                onClick={() => setApproveFilter(f.value)}
                color={approveFilter === f.value ? "primary" : "default"}
                variant={approveFilter === f.value ? "filled" : "outlined"}
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  cursor: "pointer",
                  borderColor: approveFilter === f.value ? "transparent" : "#8B1A1A",
                  color: approveFilter === f.value ? "#fff" : "#8B1A1A",
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* İzin Tipi Filtresi */}
        {uniqueTypes.length > 0 && (
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: "text.secondary" }}>
              İzin Tipi
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label="Tümü"
                onClick={() => setTypeFilter("all")}
                color={typeFilter === "all" ? "primary" : "default"}
                variant={typeFilter === "all" ? "filled" : "outlined"}
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  cursor: "pointer",
                  borderColor: typeFilter === "all" ? "transparent" : "#8B1A1A",
                  color: typeFilter === "all" ? "#fff" : "#8B1A1A",
                }}
              />
              {uniqueTypes.map((type) => (
                <Chip
                  key={type}
                  label={type}
                  onClick={() => setTypeFilter(type)}
                  color={typeFilter === type ? "primary" : "default"}
                  variant={typeFilter === type ? "filled" : "outlined"}
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    cursor: "pointer",
                    borderColor: typeFilter === type ? "transparent" : "#8B1A1A",
                    color: typeFilter === type ? "#fff" : "#8B1A1A",
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>

      {/* Grid */}
      <Box sx={{ minWidth: 600 }}>
        <DataGrid
          rows={filteredRows}
          loading={isPermissionLoading}
          disableColumnMenu
          columns={personelPermissionColumns(
            handleApproveOrReject,
            handleDocument,
            isApproveLoading,
            handleSystemForm
          )}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          disableRowSelectionOnClick
          getRowHeight={() => "auto"}
        />
      </Box>
    </Box>
  );
};

export default PersonelPermissionGrid;
